import { clsx } from 'clsx';
import type { ClassValue } from 'clsx';
import { twMerge } from 'tailwind-merge';

/**
 * Merges conditional class names and resolves Tailwind conflicts (the shadcn/ui convention, which the design
 * system follows so a component override wins over its own base classes).
 *
 * @param inputs Class values: strings, arrays, objects or falsy values.
 * @returns A single class string with later utilities replacing earlier ones.
 */
export function cn(...inputs: ClassValue[]): string {
  return twMerge(clsx(inputs));
}

/**
 * Builds up to two initials from a display name.
 *
 * The avatar fallback must not show a broken image or an empty circle, and initials survive an empty name, a single
 * word and a Persian full name equally well.
 *
 * @param name Display name of the account.
 * @returns One or two characters, or `?` when there is nothing to derive.
 */
export function initialsOf(name: string | null | undefined): string {
  const parts = (name ?? '')
    .trim()
    .split(/\s+/)
    .filter((part) => part.length > 0);

  if (parts.length === 0) {
    return '?';
  }

  // `noUncheckedIndexedAccess` widens indexed access to `string | undefined`, so the defaults keep this total.
  const first = parts[0] ?? '';
  const last = parts.length > 1 ? (parts[parts.length - 1] ?? '') : '';

  return `${first.charAt(0)}${last.charAt(0)}`.toLocaleUpperCase();
}

/**
 * Abbreviates a long identifier so it can be shown next to a label without wrapping.
 *
 * Used for API-key fingerprints and session ids: the leading and trailing characters identify the value, while the
 * middle carries no information.
 *
 * @param value Identifier to shorten.
 * @param head Number of leading characters to keep.
 * @param tail Number of trailing characters to keep.
 * @returns The shortened identifier, or the original when it is already short enough.
 */
export function abbreviate(value: string, head = 6, tail = 4): string {
  if (value.length <= head + tail + 1) {
    return value;
  }

  return `${value.slice(0, head)}…${value.slice(-tail)}`;
}
